import React from 'react';

const SkillBar = ({ skill, level, description }) => {
  // Function to convert numerical level to descriptive text
  const getLevelText = (level) => {
    if (level >= 1 && level <= 3) return "Familiar";
    if (level >= 4 && level <= 6) return "Competent";
    if (level >= 7 && level <= 8) return "Proficient";
    if (level >= 9 && level <= 10) return "Advanced";
    return "Familiar"; // fallback
  };

  return (
    <div className="skill-bar">
      <div className="skill-header">
        <span className="skill-name">{skill}</span>
        <span className="skill-level">{getLevelText(level)}</span>
      </div>
      <div className="skill-bar-bg">
        <div 
          className="skill-bar-fill"
          data-level={level}
          style={{ width: `${(level / 10) * 100}%` }}
        />
      </div>
      {description && (
        <div className="skill-description">
          {description}
        </div>
      )}
    </div>
  );
};

const SkillSection = ({ title, skills }) => {
  return (
    <div className="skill-section">
      <h3 className="skill-section-title">{title}</h3>
      {skills.map((skill, index) => (
        <SkillBar key={index} skill={skill.name} level={skill.level} description={skill.description} />
      ))}
    </div>
  );
};

const Skills = () => {
  const engineeringSkills = [
    { 
      name: 'Javascript/NodeJS/React/Typescript', 
      level: 7,
      description: 'Developed full-stack web applications using JavaScript, Node.js, and React, building dynamic user interfaces and RESTful backend APIs.'
    },
    { 
      name: 'HTML/CSS/Handlebars', 
      level: 7,
      description: 'Integral in every web app I have developped, aiding in content, styling, animations, and dynamic rendering.'
    },
    { 
      name: 'Python', 
      level: 5,
      description: 'Used in multiple apps and programs for data processing/introductory Machine Learning work/Discrete Math assignments.'
    },
    { 
      name: 'C++/C', 
      level: 3,
      description: 'Used for the back end of Arduino projects, integral in collegiate computer science lectures and exam questions.'
    },
    { 
      name: 'Relational DB/SQL/Postgres/MySQL', 
      level: 5,
      description: 'Foundational to the data collection/preservation of my web apps projects.'
    },
    { 
      name: 'Physical ComputingArduino/Teensy', 
      level: 3,
      description: 'Built a small variety of physical computing projects, often for audio integrations.'
    },
    { 
      name: 'UI/UX', 
      level: 6,
      description: 'Built a small variety of physical computing projects, often for audio integrations.'
    },
    { 
      name: 'Digital Fabrication [CAD] (Rhino, Fusion 360)', 
      level: 3,
      description: 'Designed simple enclosures for physical computing projects.'
    },
    { 
      name: 'Docker', 
      level: 3,
      description: 'Used as a container for collaborative development projects in a college course, as well as for SongSage.'
    },
    { 
      name: 'Java', 
      level: 3,
      description: 'Used in all "Intro To Computer Science" assignments.'
    },
    { 
      name: 'Git', 
      level: 5,
      description: 'Used for version control/storage of virtually every development project I have ever done.'
    },
    { 
      name: 'Agile', 
      level: 3,
      description: 'Experienced the methodology during a collaborative collegiate project.'
    }
  ];

  const artSkills = [
    { 
      name: 'Music/sound production, mixing (Ableton and other DAWs)', 
      level: 9,
      description: '10+ years of experience producing, mixing and mastering compositions with real instruments and synthesizers.'
    },
    { 
      name: 'Video production, animation, and editing (Premiere, After Effects, Blender)', 
      level: 7,
      description: 'Produced visuals for live music shows, created music videos for the Charlie Daze project.'
    },
    { 
      name: 'Photo production and editing (Photoshop, Lightroom)', 
      level: 7,
      description: 'Used on a large variety of creative and practical photography and graphic design projects.'
    },
    { 
      name: 'Graphic design (Illustrator)', 
      level: 3,
      description: 'Limited work designing concert posters.'
    }
  ];

  React.useEffect(() => {
    const skillBars = document.querySelectorAll('.skill-bar-fill');
    skillBars.forEach(bar => {
      // Get the level from the data attribute
      const level = parseInt(bar.getAttribute('data-level'));
      const targetWidth = `${(level / 10) * 100}%`;
      
      // Start with 0 width
      bar.style.width = '0%';
      
      // Animate to target width after a short delay
      setTimeout(() => {
        bar.style.width = targetWidth;
      }, 300);
    });
  }, []);

  return (
    <div className="skills-scroll-container">
      <div className="skills-content">
        <div className="skills-wrapper">
          <SkillSection title="Engineering" skills={engineeringSkills} />
          <SkillSection title="Art" skills={artSkills} />
        </div>

        <style>{`
          .skills-wrapper {
            padding: 0 15px;
            text-align: left;
          }

          .skills-scroll-container {
            position: relative;
            flex: 1;
            /* Match About.js height calculation exactly */
            height: calc(100% - 140px);
            overflow-y: auto;
            margin: 20px 8px;
            margin-top: 0;
            min-height: 0;
            scroll-behavior: smooth;
            direction: rtl;
            scrollbar-gutter: stable;
            overscroll-behavior: none; /* Stronger than 'contain' */
            touch-action: none; /* Disable browser handling of gestures to ensure we trap everything */
          }

          .skills-content {
             direction: ltr;
             margin-left: 12px;
          }

          .skills-scroll-container::-webkit-scrollbar {
            width: 8px;
            background: transparent;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .skills-scroll-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 4px;
          }

          .skills-scroll-container::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.15);
            border-radius: 4px;
            transition: background 0.3s ease;
          }

          .skills-scroll-container:hover::-webkit-scrollbar {
            opacity: 1;
          }

          .skills-scroll-container:hover::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.25);
          }

          .skill-section {
            margin-bottom: 24px;
          }

          .skill-section-title {
            font-size: 18px;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 16px;
            font-family: 'Eurostile Extended', 'Eurostile', sans-serif;
          }

          .skill-bar {
            margin-bottom: 20px;
          }

          .skill-header {
            display: flex;
            justify-content: space-between;
            align-items: center; /* Align items vertically centered */
            margin-bottom: 6px;
            gap: 15px; /* Minimum space between name and level */
            width: 100%; /* Ensure container takes full width */
            overflow: hidden; /* Prevent horizontal scroll from header overflow */
          }

          .skill-name {
            font-size: 14px;
            color: #e0e0e0;
            font-family: 'Inter', sans-serif;
            font-weight: 300;
            flex-shrink: 1; /* Allow shrinking if needed */
            margin-right: auto; /* Push level to the right */
            
            /* Add truncation styles */
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 0; /* Enable truncation for flex item */
          }

          .skill-level {
            font-size: 14px;
            color: #a0a0a0;
            font-family: 'Times New Roman', 'Times', serif;
            font-style: italic;
            font-weight: 400;
            flex-shrink: 0; /* Prevent level text from shrinking/wrapping */
            white-space: nowrap; /* Keep level text on one line */
            padding-left: 10px; /* Ensure some space between truncated name and level */
          }

          .skill-bar-bg {
            width: 100%;
            height: 4px;
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 8px;
          }

          .skill-bar-fill {
            height: 100%;
            background-color: rgba(255, 255, 255, 0.8);
            border-radius: 2px;
            transition: width 0.8s ease-out;
            width: 0%;
          }

          .skill-description {
            font-size: 12px;
            color: #b0b0b0;
            font-family: 'Inter', sans-serif;
            font-weight: 300;
            line-height: 1.4;
            margin-top: 4px;
            font-style: italic;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Skills;